import { useEffect, useState } from 'react'

import { RefetchDevicesFunction } from './useFloorPlan'
import { useToasts } from '@/hooks/useToasts'

import {
  useCreateTailgateConnMutation,
  useModifyTailgateConnMutation,
  useUpdateTailgateEnabledMutation,
} from '@/graphql/generated/hooks'
import {
  DeviceType,
  UpdateTailgateEnabledInput,
} from '@/graphql/generated/schemas'

import { ConnectionI, DeviceI } from '../types/types'
import { isDeviceInCameraRange } from '../utils/chart'

export const useDeviceConnections = (devices: DeviceI[]) => {
  const [possibleConnections, setPossibleConnections] = useState<ConnectionI[]>(
    []
  )
  const [establishedConnections, setEstablishedConnections] = useState<
    ConnectionI[]
  >([])
  const { showError } = useToasts()
  const [isLoading, setLoading] = useState(false)
  const [isEnableTailgateLoading, setIsEnableTailgateLoading] = useState(false)
  const [createTailgateConnection] = useCreateTailgateConnMutation()
  const [modifyTailgateConnection] = useModifyTailgateConnMutation()
  const [enableTailgateConnection] = useUpdateTailgateEnabledMutation()

  const _cameraInRange = (camera, device) => {
    const isCameraPlaced =
      camera.deviceType === DeviceType.Camera && camera.placed === true
    const isInRange = isDeviceInCameraRange(
      device.x,
      device.y,
      camera.x,
      camera.y,
      camera.angle,
      camera.rotation,
      camera.distance
    )
    return isCameraPlaced && isInRange
  }
  const findPossibleConnections = (): ConnectionI[] => {
    const connections: ConnectionI[] = devices
      .filter((device) => device.placed)
      .filter(
        (d) =>
          d.deviceType === DeviceType.Door || d.deviceType === DeviceType.Alarm
      )
      .map((device) => {
        const devicePossibleConnections = devices
          .filter((device) => device.placed)
          .filter((camera) => _cameraInRange(camera, device))
        return devicePossibleConnections.map((camera) => ({
          id: device.id + camera.id,
          deviceId: device.id,
          cameraId: camera.id,
          deviceType: device.deviceType,
          lineCoords: [
            [device.x, device.y],
            [camera.x, camera.y],
          ],
          isConnected: false,
          isTailgateGridConfigured: false,
          isTailgateEnabled: false,
        }))
      })
      .reduce((result, current) => result.concat(current), [])
    return connections
  }

  const findExistingConnections = (): ConnectionI[] => {
    const placedDevicesIds = devices
      .filter((device) => device.placed)
      .map((d) => d.id)

    const validCameras = devices
      .filter((device) => device.placed)
      .filter((device) => device.deviceType === DeviceType.Camera)
      .filter((device) => device.tailgateConnections.length > 0)

    const connections: ConnectionI[] = validCameras
      .map((camera) =>
        camera.tailgateConnections
          // make sure the door device is still on the map
          .filter((connection) => placedDevicesIds.includes(connection.door))
          .map((connection) => {
            const device = devices.find((d) => d.id == connection.door)
            return {
              id: connection.id,
              deviceId: device.id,
              cameraId: camera.id,
              deviceType: device.deviceType,
              lineCoords: [
                [device.x, device.y],
                [camera.x, camera.y],
              ],
              isConnected: true,
              isTailgateGridConfigured: findIsTailgateGridConfigured(
                camera,
                device.id
              ),
              isTailgateEnabled: findIsTailgateEnabled(camera, device.id),
            }
          })
      )
      .reduce((result, current) => result.concat(current), [])

    return connections
  }

  const findIsTailgateGridConfigured = (
    camera: DeviceI,
    doorId: string
  ): boolean => {
    return camera?.camera?.tailgateGrids?.some(
      (e, i) =>
        e.door.id === doorId && !!camera?.camera?.tailgateGrids?.[i].inBox
    )
  }

  const findIsTailgateEnabled = (device: DeviceI, doorId: string): boolean => {
    return device?.camera?.tailgateGrids?.some(
      (d) => d.door.id === doorId && d.isTailgateEnabled
    )
  }

  const connectDevices = async (
    camera: DeviceI,
    door: DeviceI,
    refetchDevices: RefetchDevicesFunction
  ): Promise<void> => {
    try {
      setLoading(true)
      if (door.tailgateConnections && door.tailgateConnections.length > 0) {
        const connectionId = door.tailgateConnections?.[0].id
        await modifyTailgateConnection({
          variables: {
            input: {
              id: connectionId,
              cameraIdsToAdd: [camera.id],
              cameraIdsToRemove: [],
            },
          },
        })
      } else {
        await createTailgateConnection({
          variables: {
            // Note doorId in this instance represents the device id. Can be a door or alarm
            input: { cameraIds: [camera.id], doorId: door.id },
          },
        })
      }
      await refetchDevices()
    } catch {
      showError('Error associating devices')
    } finally {
      setLoading(false)
    }
  }

  const disconnectDevices = async (
    cameraId: string,
    doorId: string,
    refetchDevices: RefetchDevicesFunction
  ): Promise<void> => {
    setLoading(true)
    const connection = establishedConnections.find(
      (connection) =>
        connection.cameraId === cameraId && connection.deviceId === doorId
    )
    const connectionId = connection.id
    try {
      await modifyTailgateConnection({
        variables: {
          input: {
            id: connectionId,
            cameraIdsToAdd: [],
            cameraIdsToRemove: [cameraId],
          },
        },
      })
      await refetchDevices()
    } catch {
      showError('Error disassociating devices')
    } finally {
      setLoading(false)
    }
  }

  const enableTailgate = async (
    input: UpdateTailgateEnabledInput,
    refetchDevices: RefetchDevicesFunction
  ): Promise<void> => {
    setIsEnableTailgateLoading(true)
    try {
      await enableTailgateConnection({
        variables: { input },
      })
      await refetchDevices()
    } catch {
      showError('Error updating tailgate')
    } finally {
      setIsEnableTailgateLoading(false)
    }
  }

  const getConnection = (cameraId: string, doorId: string) => {
    return establishedConnections
      .filter((connection) => connection.isConnected)
      .find(
        (connection) =>
          connection.cameraId === cameraId && connection.deviceId === doorId
      )
  }

  const reloadConnections = () => {
    setPossibleConnections(findPossibleConnections())
    setEstablishedConnections(findExistingConnections())
  }

  useEffect(reloadConnections, [devices.length, devices])

  return {
    connections: [...possibleConnections, ...establishedConnections],
    connectDevices,
    disconnectDevices,
    enableTailgate,
    getConnection,
    forceConnectionsRefresh: reloadConnections,
    isLoading,
    isEnableTailgateLoading,
  }
}
